import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Header } from "../components/Header";
import SubscriptionNotice from "../components/SubscriptionNotice";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Disclaimer } from "../components/Disclaimer";
import { toast } from "sonner";
import { Fire, SignOut, Star, Medal, Function, CaretRight, Crown, Gear, Trophy, EnvelopeSimple, ChartLineUp, Storefront } from "@phosphor-icons/react";
import { manageNativeSubscriptions, STORE_NAMES } from "../lib/iap";

const LEVEL_COLORS = { Rookie: "#43A047", Analyst: "#00E676", Expert: "#FB8C00", Samurai: "#D4AF37" };

export default function Profile() {
  const { t, i18n } = useTranslation();
  const locale = { fr: "fr-FR", en: "en-GB", es: "es-ES" }[i18n.language?.slice(0, 2)] || "fr-FR";
  const { logout, user, subscription } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [favDetails, setFavDetails] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const [verifySending, setVerifySending] = useState(false);

  const resendVerification = async () => {
    setVerifySending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(t("profile.verify_sent"));
    } catch {
      toast.error(t("profile.portal_error"));
    } finally { setVerifySending(false); }
  };

  const manageSubscription = async (provider) => {
    setPortalLoading(true);
    try {
      await manageNativeSubscriptions(provider);
    } catch (e) {
      toast.error(e?.message || t("profile.portal_error"));
    } finally { setPortalLoading(false); }
  };

  useEffect(() => {
    api.get("/profile").then(async (r) => {
      setP(r.data);
      const teams = r.data.favorites.filter((f) => f.kind === "team");
      const details = await Promise.all(teams.map((f) => api.get(`/teams/${f.ref_id}`).then((x) => x.data).catch(() => null)));
      setFavDetails(details.filter(Boolean));
    });
  }, []);

  if (!p) return (<><Header showSearch={false} /><div className="px-5 pt-6 space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 rounded-2xl skeleton" />)}</div></>);
  const lvl = p.level;
  const color = LEVEL_COLORS[lvl.name] || "#D4AF37";

  return (
    <>
      <Header showSearch={false} />
      <div className="px-5 pt-5">
        <SubscriptionNotice />
        <h1 className="font-display text-2xl font-bold tracking-tight">{t("profile.title")}</h1>

        <div className="hm-card p-5 mt-4" data-testid="profile-card">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <div className="w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center font-numeral font-black text-2xl"
                style={{ background: `${color}22`, border: `1px solid ${color}66`, color }}>
                {p.avatar_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${p.avatar_url}`} alt="" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
                ) : (
                  p.name?.[0]?.toUpperCase()
                )}
              </div>
              {p.season_titles > 0 && (
                <div data-testid="champion-crown" title={p.champion_seasons?.join(", ")}
                  className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gold flex items-center justify-center border-2 border-[#141414]">
                  <Crown size={14} weight="fill" color="#000" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold break-words">{p.name}</p>
              <p className="text-xs text-white/40 truncate">{p.email}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                {p.season_titles > 0 && (
                  <span data-testid="champion-badge" title={p.champion_seasons?.join(", ")}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-gold/18 text-gold border border-gold/40">
                    <Crown size={11} weight="fill" /> {t("profile.champion", { count: p.season_titles })}
                  </span>
                )}
                {user?.plan === "free" && (
                  <span data-testid="not-subscribed-badge"
                    className="inline-block px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-white/8 text-white/55 border border-white/10">
                    {t("profile.not_subscribed")}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1 px-2.5 py-1.5 rounded-full bg-white/5 whitespace-nowrap" title={t("profile.streak_tooltip")} data-testid="streak-badge">
              <Fire size={16} weight="fill" color="#FB8C00" />
              <span className="font-numeral font-bold text-sm" data-testid="streak">{p.streak}</span>
              <span className="text-[10px] text-white/45 font-semibold">{t("profile.streak_unit")}</span>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-1.5 text-sm font-bold" style={{ color }}>
                <Medal size={16} weight="fill" /> {lvl.name}
              </span>
              <span className="text-xs text-white/50">{lvl.xp} XP{lvl.next ? ` · ${t("profile.next_at", { next: lvl.next, at: lvl.next_at })}` : ` · ${t("profile.max")}`}</span>
            </div>
            <Progress value={lvl.progress} className="h-2 bg-white/10" />
          </div>
        </div>

        <button
          data-testid="manage-account-link"
          onClick={() => navigate("/compte")}
          className="w-full p-4 mt-3 flex items-center gap-3 text-left rounded-2xl border border-white/10 bg-[#121212] hover:bg-white/5 transition-colors"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gold/15 border border-gold/40">
            <Gear size={18} weight="fill" color="#D4AF37" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold">{t("profile.manage_account.title")}</p>
            <p className="text-[11px] text-white/40">{t("profile.manage_account.desc")}</p>
          </div>
          <CaretRight size={18} color="rgba(255,255,255,0.4)" />
        </button>

        <button
          data-testid="logout-btn-top"
          onClick={logout}
          className="w-full mt-3 h-11 flex items-center justify-center gap-2 rounded-2xl border border-red-500/30 bg-red-500/10 text-red-300 font-bold text-sm hover:bg-red-500/20 transition-colors"
        >
          <SignOut size={17} weight="bold" /> {t("profile.logout")}
        </button>

        <button
          data-testid="passer-pro-link"
          onClick={() => navigate("/passer-pro")}
          className="w-full p-4 mt-4 flex items-center gap-3 text-left rounded-2xl border transition-colors"
          style={user?.plan === "pro" || user?.plan === "vip"
            ? { background: "linear-gradient(120deg, rgba(212,175,55,0.12), rgba(18,18,18,1))", borderColor: "rgba(212,175,55,0.4)" }
            : { background: "#121212", borderColor: "rgba(212,175,55,0.3)" }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gold/15 border border-gold/50">
            <Crown size={18} weight="fill" color="#D4AF37" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold flex items-center gap-2">
              {t("profile.subscription")}
              <span data-testid="plan-badge" className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-gold/18 text-gold">
                {t(`profile.plan_${user?.plan}`)}
              </span>
            </p>
            <p className="text-[11px] text-white/40">
              {user?.plan === "vip" ? t("profile.plan_vip_desc")
                : user?.plan === "pro" ? t("profile.plan_pro_desc")
                : user?.plan === "starter" ? t("profile.plan_starter_desc")
                : t("profile.plan_free_desc")}
            </p>
          </div>
          <CaretRight size={18} color="rgba(255,255,255,0.4)" />
        </button>

        {(subscription?.subscriptions || []).map((subscription) => (
          <button
            key={subscription.provider}
            data-testid="manage-subscription-link"
            onClick={() => manageSubscription(subscription.provider)}
            disabled={portalLoading}
            className="w-full p-4 mt-3 flex items-center gap-3 text-left rounded-2xl border border-white/10 bg-[#121212] transition-colors disabled:opacity-60"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/5 border border-white/10">
              <Gear size={18} weight="fill" color="rgba(255,255,255,0.6)" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold">{portalLoading ? t("profile.portal_opening") : t("profile.manage_sub")}</p>
              <p className="text-[11px] text-white/40">
                {STORE_NAMES[subscription.provider]} · {subscription.plan.toUpperCase()} · {subscription.active ? "Actif" : "Inactif"}
                {subscription.active && (subscription.grace_expires_at || subscription.expires_at) && ` · Accès jusqu’au ${new Date(subscription.grace_expires_at || subscription.expires_at).toLocaleDateString(locale)}`}
                {subscription.cancel_at_period_end && " · Renouvellement annulé"}
                {subscription.provider === "apple" && subscription.auto_renew === true && " · Renouvellement automatique activé"}
                {subscription.active && subscription.grace_expires_at && " · Période de grâce"}
                {subscription.billing_retry && " · Paiement à régulariser dans l’App Store"}

              </p>
            </div>
            <CaretRight size={18} color="rgba(255,255,255,0.4)" />
          </button>
        ))}

        {p.role === "admin" && (
          <button
            data-testid="admin-moderation-link"
            onClick={() => navigate("/admin/moderation")}
            className="w-full p-4 mt-3 flex items-center gap-3 text-left rounded-2xl border border-white/10 bg-[#121212]"
          >
            <div className="flex-1">
              <p className="text-sm font-bold">Modération</p>
              <p className="text-[11px] text-white/40">Signalements de pseudos, avatars et ligues</p>
            </div>
            <CaretRight size={16} color="rgba(255,255,255,0.4)" />
          </button>
        )}
        {p.role === "admin" && (
          <button
            data-testid="admin-vip-link"
            onClick={() => navigate("/admin/vip")}
            className="w-full p-4 mt-3 flex items-center gap-3 text-left rounded-2xl border border-gold/30 bg-[#121212]"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gold/15 border border-gold/40">
              <Function size={18} weight="fill" color="#D4AF37" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold">{t("profile.admin_vip")}</p>
              <p className="text-[11px] text-white/40">{t("profile.admin_vip_desc")}</p>
            </div>
            <CaretRight size={18} color="rgba(255,255,255,0.4)" />
          </button>
        )}

        {p.role === "admin" && (
          <button
            data-testid="admin-analytics-link"
            onClick={() => navigate("/admin/analytics")}
            className="w-full p-4 mt-3 flex items-center gap-3 text-left rounded-2xl border border-gold/30 bg-[#121212]"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gold/15 border border-gold/40">
              <ChartLineUp size={18} weight="fill" color="#D4AF37" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold">{t("profile.admin_analytics")}</p>
              <p className="text-[11px] text-white/40">{t("profile.admin_analytics_desc")}</p>
            </div>
            <CaretRight size={18} color="rgba(255,255,255,0.4)" />
          </button>
        )}

        {p.role === "admin" && (
          <button
            data-testid="admin-partners-link"
            onClick={() => navigate("/admin/partenaires")}
            className="w-full p-4 mt-3 flex items-center gap-3 text-left rounded-2xl border border-gold/30 bg-[#121212]"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gold/15 border border-gold/40">
              <Storefront size={18} weight="fill" color="#D4AF37" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold">Partenaires</p>
              <p className="text-[11px] text-white/40">Tabacs & influenceurs · QR, chevalets, commissions</p>
            </div>
            <CaretRight size={18} color="rgba(255,255,255,0.4)" />
          </button>
        )}

        <button
          data-testid="methode-link"
          onClick={() => navigate("/methode")}
          className="w-full hm-card p-4 mt-4 flex items-center gap-3 text-left hover:hm-card-active transition-colors"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gold/12 border border-gold/40">
            <Function size={18} weight="fill" color="#D4AF37" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold">{t("profile.method")}</p>
            <p className="text-[11px] text-white/40">{t("profile.method_desc")}</p>
          </div>
          <CaretRight size={18} color="rgba(255,255,255,0.4)" />
        </button>

        <button
          data-testid="rules-game-link"
          onClick={() => navigate("/reglement-tirage")}
          className="w-full hm-card p-4 mt-3 flex items-center gap-3 text-left hover:hm-card-active transition-colors"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gold/12 border border-gold/40">
            <Trophy size={18} weight="fill" color="#D4AF37" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold">{t("profile.game_rules")}</p>
            <p className="text-[11px] text-white/40">{t("profile.game_rules_desc")}</p>
          </div>
          <CaretRight size={18} color="rgba(255,255,255,0.4)" />
        </button>

        {p.email_verified === false && (
          <div data-testid="email-verify-row" className="w-full hm-card p-4 mt-3 flex items-center gap-3"
            style={{ border: "1px solid rgba(212,175,55,0.4)" }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gold/12 border border-gold/40">
              <EnvelopeSimple size={18} weight="fill" color="#D4AF37" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold">{t("profile.verify_email")}</p>
              <p className="text-[11px] text-white/40">{t("profile.verify_email_desc")}</p>
            </div>
            <button data-testid="profile-resend-verify" onClick={resendVerification} disabled={verifySending}
              className="text-[12px] font-bold text-gold hover:underline disabled:opacity-60 shrink-0">
              {verifySending ? "…" : t("profile.resend")}
            </button>
          </div>
        )}
        {favDetails.length ? (
          <div className="grid grid-cols-2 gap-2.5">
            {favDetails.map((tm) => (
              <button key={tm.team_id} data-testid={`fav-${tm.team_id}`} onClick={() => navigate(`/club/${tm.team_id}`)}
                className="hm-card p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-numeral font-bold text-xs text-white"
                  style={{ background: tm.color }}>{tm.short}</div>
                <span className="text-sm font-semibold flex-1 text-left">{tm.name}</span>
                <Star size={16} weight="fill" color="#D4AF37" />
              </button>
            ))}
          </div>
        ) : (
          <div className="hm-card p-4 text-center text-sm text-white/40" data-testid="no-favs">
            {t("profile.no_favs")}
          </div>
        )}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mt-8 mb-3">{t("profile.quiz_history")}</p>
        <div className="hm-card overflow-hidden" data-testid="quiz-history">
          {p.quiz_history.length ? p.quiz_history.slice().reverse().map((h, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 last:border-0">
              <span className="text-sm text-white/60">{new Date(h.date).toLocaleDateString(locale)}</span>
              <span className="font-numeral font-bold text-gold">{h.score}/{h.total}</span>
              <span className="text-xs text-white/40">+{h.xp} XP</span>
            </div>
          )) : <p className="text-sm text-white/40 p-4 text-center">{t("profile.no_quiz")}</p>}
        </div>

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mt-8 mb-3">{t("profile.score_history")}</p>
        <div className="hm-card overflow-hidden" data-testid="score-history">
          {p.score_history.length ? p.score_history.slice().reverse().map((h, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 last:border-0">
              <span className="text-sm text-white/60">{new Date(h.date).toLocaleDateString(locale)}</span>
              <span className="text-xs text-white/40">{t("profile.predictions_count", { count: h.predictions.length })}</span>
              <span className="text-xs text-gold">+{h.xp} XP</span>
            </div>
          )) : <p className="text-sm text-white/40 p-4 text-center">{t("profile.no_score")}</p>}
        </div>

        <Button data-testid="logout-btn" onClick={logout} variant="outline"
          className="w-full mt-8 h-12 border-white/15 bg-transparent text-white/70 hover:bg-white/5 hover:text-white">
          <SignOut size={18} weight="bold" className="mr-2" /> {t("profile.logout")}
        </Button>
        <Disclaimer className="mt-6" />
        <div className="h-8" />
      </div>
    </>
  );
}
